import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { v4 as uuidv4 } from "uuid";
import { WidgetTabHeaderDataTypes } from "@lifeat/shared/data-models/user/UserEntity";
import { WidgetTabType } from "@lifeat/shared/data-models/widget/WidgetTab/WidgetTabDataModel";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import { CallToActionContextType, useCallToActionContext } from "@contexts/CallToActionContext";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { logError } from "@service/ConsoleLoggerService";
import { UpgradeCTASource } from "@user/AmplitudeProSubsEvents";
import { CreateNoteEvent } from "../../../../AmplitudeWidgetEvents";
import * as WidgetTabService from "../../../../service/WidgetTabService";
import WidgetTabSelector, { WidgetTabSelectorElement } from "./WidgetTabSelector";

const WidgetTabWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`;

const DEFAULT_MAX_TAB_NAME_LENGTH = 30;
const INVALID_TAB_NAME_ERROR_MSG = "Please input a title for your note.";

/**
 * Data needed for tab dropdown selector.
 */
interface WidgetTabHeaderProps {
    /**
     * Determines the widget context for this tab (e.g. DB reads/writes)
     */
    readonly widgetId: WidgetTabHeaderDataTypes;
    /**
     * The tab that is currently selected
     */
    readonly activeTabId: string;
    /**
     * Logic to execute when selecting a new tab
     */
    readonly setActiveTabId: (id: string) => void;
    /**
     * Determines what text shows off the new tab button
     */
    readonly newTabLabel: string;
    /**
     * Implementors of this component will need to determine what the max number
     * of tabs a user can create is based on business logic in the implementing
     * component.
     */
    readonly maxTabsForUser: number;
}
export default function WidgetTabHeader(props: WidgetTabHeaderProps) {
    const { setUpgradeCtaContext }: CallToActionContextType = useCallToActionContext();
    const { userData }: UserContextType = useUserContext();

    const amplitudeService = AmplitudeService.getInstance();

    const [handleUpdateIsLoading, setHandleUpdateIsLoading] = useState(false);
    const [onCreateIsLoading, setOnCreateIsLoading] = useState(false);
    const [onDeleteIsLoading, setOnDeleteIsLoading] = useState(false);

    const [tabs, setTabs] = useState<{ [id: string]: WidgetTabType }>({});
    useEffect(() => {
        const widgetData = userData?.[props.widgetId];
        // @ts-expect-error NoteType doesn't satisfy WidgetTabType until strict mode is enabled.
        const allTabs = sortTabs(widgetData?.tabs) || {};

        if (JSON.stringify(tabs) !== JSON.stringify(allTabs)) {
            if (allTabs) {
                setTabs(allTabs);
            }
        }

        // Initialize activeTabId if null or invalid
        let tabList: WidgetTabType[] = Object.values(allTabs);
        // If there's no widgetData or activeTabId, either we have not initialized
        //  tab data for the user, or it simply was not set.
        if (!widgetData?.activeTabId) {
            if (tabList && tabList.length > 0) {
                // No active tab, available options
                handleSelectWidgetTab(tabList[0].id, allTabs);
            } else if (canCreateNewTab(allTabs)) {
                // No active tab, no options
                const tab = handleCreateNewWidgetTab(props.newTabLabel, tabs);
                handleSelectWidgetTab(tab.id, allTabs);
            }
        } else if (!Object.keys(allTabs).includes(widgetData.activeTabId)) {
            // Somehow user data is in bad state where activeTabId is set, but there are no tabs
            // saved. So we create a new one and update the activeTabId.
            if ((!allTabs || Object.keys(allTabs).length === 0) && canCreateNewTab(allTabs)) {
                const tab = handleCreateNewWidgetTab(props.newTabLabel, tabs);
                handleSelectWidgetTab(tab.id, allTabs);
            } else {
                // Select the first tab in the case where the activeTabId is invalid.
                handleSelectWidgetTab(tabList[0]?.id, allTabs);
            }
        }
    }, [userData?.[props.widgetId]]);

    return userData && tabs && Object.keys(tabs).includes(props.activeTabId) ? (
        <WidgetTabWrapper>
            <WidgetTabSelector
                activeTab={tabs[props.activeTabId]}
                canCreateNewTab={canCreateNewTab(tabs)}
                createNewTab={(name: string) => handleCreateNewWidgetTab(name, tabs)}
                newTabLabel={props.newTabLabel}
                showCreateIsLoading={onCreateIsLoading}
                handleInvalidCreateTabClick={handleDisallowedCreateNewTab}
            >
                {Object.values(tabs).map((tab: WidgetTabType) => {
                    return (
                        <WidgetTabSelectorElement
                            key={`${props.widgetId}-tab-${tab.id}`}
                            tab={tab}
                            isActive={tab.id === props.activeTabId}
                            setActiveTab={() => handleSelectWidgetTab(tab.id, tabs)}
                            editTabName={(newName: string) => handleEditWidgetTabName(tab.id, newName, tabs)}
                            canDeleteTab={Object.keys(tabs).length > 1}
                            deleteTab={() => handleDeleteWidgetTab(tab.id, tabs)}
                            maxTabNameLength={DEFAULT_MAX_TAB_NAME_LENGTH}
                            showUpdateLoading={handleUpdateIsLoading}
                            showDeleteLoading={onDeleteIsLoading}
                        />
                    );
                })}
            </WidgetTabSelector>
        </WidgetTabWrapper>
    ) : null;

    function handleSelectWidgetTab(tabId: string, tabs: { [id: string]: WidgetTabType }) {
        if (tabs[tabId]) {
            props.setActiveTabId(tabId);
            WidgetTabService.selectWidgetTab(props.widgetId, { id: tabId });
        }
    }

    function handleEditWidgetTabName(tabId: string, newTabName: string, tabs: { [id: string]: WidgetTabType }) {
        if (newTabName?.length === 0) {
            alert(INVALID_TAB_NAME_ERROR_MSG);
        } else {
            if (tabs[tabId]) {
                setHandleUpdateIsLoading(true);
                const updatedTab: WidgetTabType = {
                    id: tabId,
                    name: newTabName,
                    lastModified: Date.now(),
                };

                WidgetTabService.editWidgetTabName(props.widgetId, updatedTab)
                    .then(() => {
                        // Successfully edited the widget tab's name
                        setHandleUpdateIsLoading(false);

                        // Update the client-side data after backend updates occur
                        const tabsCopy = { ...tabs };
                        tabsCopy[tabId].name = newTabName;
                        const sortedTabs = sortTabs(tabsCopy);
                        setTabs(sortedTabs);
                    })
                    .catch((error) => {
                        setHandleUpdateIsLoading(false);
                        logError(
                            `There was an error [code: ${error.code}, message: ${error.message}] ` +
                                `when editing the widget tab's name.`,
                        );
                    });
            }
        }
    }

    function handleDeleteWidgetTab(tabId: string, tabs: { [id: string]: WidgetTabType }) {
        // Only allow deleting a list if the user has more than one list
        if (tabs[tabId] && Object.keys(tabs).length > 1) {
            setOnDeleteIsLoading(true);

            // If the user deletes the active tab, select the one after it
            let newActiveTabId: string = undefined;
            if (tabId === props.activeTabId) {
                const allTabsIdList = Object.keys(tabs);
                const tabIndex = allTabsIdList.indexOf(tabId);
                if (tabIndex < allTabsIdList.length - 1) {
                    newActiveTabId = allTabsIdList[tabIndex + 1];
                } else {
                    newActiveTabId = allTabsIdList[0];
                }

                // NOTE: State is updated after the DB is updated to avoid race condition
                //  with attempting to update tab content while attempting to delete tab.
                //  See SlateEditor.tsx for example of this in useEffect hook.
            }

            WidgetTabService.deleteWidgetTab(props.widgetId, { id: tabId })
                .then((res) => {
                    setOnDeleteIsLoading(false);
                    if (newActiveTabId && res.data) {
                        // Update state after DB has updated successfully.
                        handleSelectWidgetTab(newActiveTabId, tabs);

                        // Update the client-side data while backend updates occur
                        const tabsCopy = { ...tabs };
                        delete tabsCopy[tabId];
                        setTabs(tabsCopy);
                    }
                })
                .catch((error) => {
                    setOnDeleteIsLoading(false);
                    logError(
                        `There was an error [code: ${error.code}, message: ${error.message}] ` +
                            `when deleting the specified widget tab.`,
                    );
                });
        }
    }

    function sortTabs(unsortedTabs: { [id: string]: WidgetTabType }): { [id: string]: WidgetTabType } | {} {
        if (unsortedTabs) {
            return Object.entries(unsortedTabs)
                .sort(([, a], [, b]) => b.lastModified - a.lastModified)
                .reduce((r, [k, v]) => ({ ...r, [k]: v }), {});
        }
        return {};
    }

    /**
     * Let us know if a user can create a new tab based on their data.
     * Both Free and Premium users can create multiple tabs, but Premium users can
     * create more.
     * @return {boolean} True if a new tab can be created
     */
    function canCreateNewTab(tabs: { [id: string]: WidgetTabType }): boolean {
        let numTabs = tabs ? Object.keys(tabs).length : 0;
        return numTabs < props.maxTabsForUser;
    }

    function handleCreateNewWidgetTab(tabName: string, tabs: { [id: string]: WidgetTabType }): WidgetTabType {
        let newTab: WidgetTabType = null;
        if (canCreateNewTab(tabs)) {
            setOnCreateIsLoading(true);

            newTab = {
                id: `${props.widgetId}-${uuidv4()}`,
                name: tabName,
                lastModified: Date.now(),
            };

            WidgetTabService.createWidgetTab(props.widgetId, newTab)
                .then(() => {
                    setOnCreateIsLoading(false);
                    // Update the client-side data while backend updates occur
                    const tabsCopy = { ...tabs };
                    tabsCopy[newTab.id] = newTab;
                    const sortedTabs = sortTabs(tabsCopy);
                    setTabs(sortedTabs);
                    handleSelectWidgetTab(newTab.id, sortedTabs);

                    // Log amplitude event when a new note is created
                    amplitudeService.logEvent(new CreateNoteEvent(Object.keys(tabsCopy).length));
                })
                .catch((error) => {
                    setOnCreateIsLoading(false);
                    logError(
                        `There was an error [code: ${error.code}, message: ${error.message}] ` +
                            `when creating the specified widget tab.`,
                    );
                });
        }

        return newTab;
    }

    function handleDisallowedCreateNewTab() {
        setUpgradeContextForWidgetId();
    }

    /**
     * TODO: duplicated in ReadOnlyIndicator.tsx, consolidate at some point.
     * - create widgetId type instead of using string
     */
    function setUpgradeContextForWidgetId() {
        switch (props.widgetId) {
            case "notesData":
                setUpgradeCtaContext("notes", UpgradeCTASource.NOTES_WIDGET);
                break;
            default:
                setUpgradeCtaContext("default", UpgradeCTASource.WIDGETS_GENERAL);
                break;
        }
    }
}
